"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-foreground/10 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.label === 'REQUEST QUOTE RFQ' ? '#request-quote-home' : item.href || '#'} className="font-serif text-[13px] uppercase tracking-wider text-foreground relative py-2 after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-full hover:text-foreground focus:text-foreground focus:bg-transparent hover:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-foreground">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/98 backdrop-blur-md border-t border-border shadow-2xl lg:hidden flex flex-col animate-in fade-in slide-in-from-top-2 duration-300">
      <div className="w-full px-6 py-8 flex flex-col gap-6">
        {loading ? <div className="flex flex-col gap-6">
            {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-foreground/10 animate-pulse rounded" />)}
          </div> : <nav className="flex flex-col gap-1">
            {menuItems.map((item, i) => <div key={item.id} data-index={i}>
                <Link to={item.label === 'REQUEST QUOTE RFQ' ? '#request-quote-home' : item.href || '#'} className="font-serif text-base uppercase tracking-widest text-foreground block py-4 border-b border-border/30 hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
                  {item.label}
                </Link>
              </div>)}
          </nav>}
        <div className="pt-2 pb-4">
          <Button variant="outline" size="lg" className="w-full border-foreground/20 text-foreground hover:bg-foreground hover:text-background font-serif uppercase tracking-wider text-[13px] h-12" asChild>
            <Link to="/SupplyQuotePage" onClick={() => setIsOpen(false)}>
              Contact Brokerage Support
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function GlobalNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <>
      <header id="global-navigation" className="dark sticky top-0 z-50 w-full transition-all duration-300 bg-background !text-[hsl(226,71%,40%)]">
        <div className="container mx-auto px-6 h-14 lg:h-20 flex items-center justify-between">
          {}
          <Link to="/" className="flex-shrink-0 z-10 flex items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1576388/img/venchandlers_nexus_maritime_brokerage_logo.png" alt="Venchandlers Nexus - Maritime Brokerage and Coordination Company" className="h-10 object-contain !text-[hsl(226,71%,40%)] ![margin:0px_10px_0px_10px] lg:h-14" />
          </Link>

          {}
          <div className="hidden lg:flex flex-1 justify-center">
            <MenuProvider menu_id="16">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="hidden lg:flex flex-shrink-0 z-10">
            <Button variant="ghost" className="text-foreground hover:text-primary hover:bg-transparent font-serif uppercase text-[13px] tracking-wider transition-colors" asChild>
              
            </Button>
          </div>

          {}
          <Button variant="ghost" size="icon" className="lg:hidden text-foreground z-10 hover:bg-accent/10 hover:text-primary border border-border/10 -mr-2 transition-all" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>

          {}
          <MenuProvider menu_id="16">
            <MobileMenuConsumer isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
          </MenuProvider>
        </div>
      </header>

      <Link to="https://wa.me/34602505574?text=Welcome%2C%20how%20can%20I%20assist%20you%3F" newTab className="fixed bottom-6 right-6 z-[60] flex items-center justify-center">
        <Button className="size-14 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white shadow-2xl border-none transition-all duration-300 hover:scale-110 active:scale-95" size="icon">
          <MessageCircle className="size-7" />
          <span className="sr-only">Contact on WhatsApp</span>
        </Button>
      </Link>
    </>;
}